// Redeclare forwardRef
import { logger } from '@gik/analytics/utils/logger';
import type { UseStreamSWROptions } from '@gik/api/stream/useStream';
import type { BaseAPIResponseError } from '@gik/core/api/BaseAPIConfig';
import { timeoutDefaultValue } from '@gik/core/constants';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import type {
  FlatActivity,
  GetFeedOptions,
  Reaction,
  StreamComment,
  UserUpdateActivity,
} from '@gik/stream/types/Stream';
import { Button } from '@gik/ui/Button';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import deepMerge from 'deepmerge';
import { StreamApiError } from 'getstream';
import React from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useLatest from 'react-use/lib/useLatest';
import type { SWRResponse } from 'swr';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode';

declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export type StreamPaginationOrder = 'asc' | 'desc';

export type PaginationEntry = FlatActivity | Reaction<StreamComment> | UserUpdateActivity;

export type StreamPaginationSetErrorCallback = (id: string, errorId: string) => void;
export type StreamPaginationAddItemCallback<D = unknown> = (data: D) => void;
export type StreamPaginationUpdateItemCallback<D = unknown> = (id: string, data: D) => void;
export type StreamPaginationDeleteItemCallback = (id: string) => void;
export type StreamPaginationReloadItemCallback = (id: string) => void;
export type StreamPaginationLoadNewerItemsCallback<D = unknown> = () => Promise<D>;
export type StreamPaginationLoadLatestItemsCallback = () => Promise<void>;

export type StreamPaginationRenderCallbackProps<D = unknown> = {
  data: D;
  sanitizedData: D;
  isLoading: boolean;
  perPage: number;
  currentPosition: number;
  cursorPos: number;
  endReached: boolean;
  reloadItem?: StreamPaginationReloadItemCallback;
  addItem?: StreamPaginationAddItemCallback;
  updateItem?: StreamPaginationUpdateItemCallback;
  deleteItem?: StreamPaginationDeleteItemCallback;

  loadOlder?: () => void;
  loadOlderItems?: () => void;
  loadNewerItems?: StreamPaginationLoadNewerItemsCallback<D>;
  loadLatestItems?: StreamPaginationLoadLatestItemsCallback;
  errors?: Record<string, string>;
  setError?: StreamPaginationSetErrorCallback;
};

export type StreamPaginationProps<D extends PaginationEntry, E extends BaseAPIResponseError = Error> = {
  perPage?: number;
  initialTotal?: number;
  showTotal?: boolean;
  newerIsReversed?: boolean;
  order?: StreamPaginationOrder;
  initialData?: D[];
  // makes sure we initially have enough items to display if some were hidden in the initialData
  // note: this is disabled by default
  initialFetch?: boolean;
  childrenOnly?: boolean;
  emptyDisplay?: () => React.ReactNode;
  /**
   * An id to of a comment to highlight
   */
  highlightedId?: string;
  debug?: boolean;
  verbose?: boolean;
  hasPrev?: boolean;
  hasNextButton?: boolean;
  sanitizeData?: (data: D[]) => D[];
  fetch(streamOptions: UseStreamSWROptions): Promise<D[]>;
  // fetch(streamOptions: UseStreamSWROptions): APIResponseInterface<D[], E>;
  fetchSingle?(id: string): SWRResponse<D, E>;
  prevButton?: (options: {
    isLoading?: boolean;
    // isValidating: boolean;
    onClick?: () => void;
    data?: D[];
    total?: number;
    loadError?: string;
    currentPosition?: number;
    currentPos?: number;
  }) => React.PropsWithChildren<{}>['children'];
  nextButton?: (options: {
    isLoading?: boolean;
    // isValidating: boolean;
    onClick?: () => void;
    data?: D[];
    total?: number;
    loadError?: string;
    currentPosition?: number;
    currentPos?: number;
  }) => React.PropsWithChildren<{}>['children'];
  header?: (options: StreamPaginationRenderCallbackProps<D[]>) => React.PropsWithChildren<{}>['children'];
  footer?: (options: StreamPaginationRenderCallbackProps<D[]>) => React.PropsWithChildren<{}>['children'];
  children?: (options: StreamPaginationRenderCallbackProps<D[]>) => React.PropsWithChildren<{}>['children'];
  onReady?: () => void;
  onChange?: (items: D[], total: number) => void;
} & UIComponent;

export type StreamPaginationType<D = unknown> = {
  loadLatestItems?: StreamPaginationLoadLatestItemsCallback;
  loadNewerItems?: StreamPaginationLoadNewerItemsCallback<D>;
  reloadItem?: StreamPaginationReloadItemCallback;
};

function StreamPaginationComp<D extends PaginationEntry, E extends BaseAPIResponseError = Error>(
  {
    children,
    className,
    initialTotal,
    showTotal,
    initialData,
    initialFetch,
    emptyDisplay,
    perPage = 5,
    newerIsReversed,
    prevButton,
    nextButton,
    hasPrev,
    hasNextButton = true,
    order = 'asc',
    highlightedId,
    debug,
    verbose,
    childrenOnly,
    sanitizeData,
    fetch,
    fetchSingle,
    header,
    footer,
    onReady,
    onChange,
    ...otherProps
  }: React.PropsWithChildren<StreamPaginationProps<D, E>>,
  ref
): React.ReactElement {
  const bem = useBemCN('stream-pagination');

  const [data, setData] = React.useState<D[]>();
  const latestData = useLatest<D[]>(data);

  const [sanitizedData, setSanitizedData] = React.useState<D[]>();
  const latestSanitizedData = useLatest<D[]>(sanitizedData);

  // need to keep track of our own total number so we can optimistically update it
  const [total, setTotal] = React.useState<number>(initialTotal);
  const latestTotal = useLatest<number>(total);

  const [currentPosition, setCurrentPosition] = React.useState<number>(0);
  const latestCurrentPosition = useLatest<number>(currentPosition);

  const [sanitizedPosition, setSanitizedPosition] = React.useState<number>(0);

  const [sanitizedCursorPosition, setSanitizedCursorPosition] = React.useState<number>();
  const latestSanitizedCursorPosition = useLatest<number>(sanitizedCursorPosition);

  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const latestHasMore = useLatest<boolean>(hasMore);

  // keep track of errors for each entry
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [shouldSkipLoadMore, setShouldSkipLoadMore] = React.useState<boolean>(false);
  const [loadError, setLoadError] = React.useState<string>();

  // only used if we don't have the number of total entries
  const [endReached, setEndReached] = React.useState<boolean>(false);
  const latestEndReached = useLatest<boolean>(endReached);

  React.useImperativeHandle(ref, () => ({
    loadNewerItems,
    reloadItem,
    loadLatestItems,
  }));

  const sanitizeDataFallback = React.useCallback((items: D[]) => {
    return items;
  }, []);

  const _sanitizeData = React.useMemo(() => sanitizeData || sanitizeDataFallback, [sanitizeData, sanitizeDataFallback]);

  const doFetch = React.useCallback(
    async (streamOptions: UseStreamSWROptions) => {
      setIsLoading(true);
      setLoadError(undefined);

      try {
        const response = await fetch(streamOptions);

        const end = response?.length < perPage;
        setEndReached(end);

        setIsLoading(false);
        return response;
      } catch (err) {
        logger.error(err);

        switch (true) {
          case err instanceof StreamApiError:
            setLoadError(err.response?.data?.detail);
            break;
          case err instanceof SyntaxError: // tries to json parse null response that is only obtainable in a timeout
            setLoadError('Connection timed out.');
            break;
          default:
            setLoadError('Unknown Error');
        }

        setIsLoading(false);
        return null;
      }
    },
    [perPage, fetch, setEndReached]
  );

  const updateHasMore = React.useCallback(() => {
    const data = latestData.current;
    const total = latestTotal.current;

    let _hasMore = false;
    if (total !== undefined) {
      // if we know the total count then we have more data to load
      // as long as data.length is less than total
      _hasMore = data ? data.length < total : false;
    }

    setHasMore(_hasMore);
  }, [setHasMore, latestTotal, latestData]);

  React.useEffect(() => {
    updateHasMore();
    // eslint-disable-next-line
  }, [latestData.current, latestTotal.current, updateHasMore]);

  const setNewData = React.useCallback(
    (newData: D[]) => {
      setData(newData);
      const sanitizedData = _sanitizeData(newData);
      setSanitizedData(sanitizedData);
      onChange?.(sanitizedData, latestTotal.current);
    },
    [_sanitizeData, onChange, latestTotal]
  );

  const addData = React.useCallback(
    (dataToAdd: D[] = [], addToStart?: boolean) => {
      // const filtered = dataToAdd.filter(item => !dataToAdd?.find(item2 => item2?.id === item?.id));

      const newItems = dataToAdd?.filter(item => !latestData.current?.find(item2 => item2?.id === item?.id));
      if (newItems) {
        const newData = addToStart
          ? newItems.concat(latestData.current || [])
          : (latestData.current || []).concat(newItems);

        setNewData(newData.filter(item => !item?.id?.startsWith('new'))); // this is it
      }
    },
    [setNewData, latestData]
  );

  const updateData = React.useCallback(
    async (dataToUpdate: D[] = []) => {
      // const filtered = dataToAdd.filter(item => !dataToAdd?.find(item2 => item2?.id === item?.id));

      const newData = (data || []).map(entry => {
        const match = dataToUpdate?.find(item => item.id === entry.id);
        if (match) {
          return match;
        }
        return entry;
      });
      setNewData(newData);
    },
    [data, setNewData]
  );

  /**
   * Load latest items from api
   */
  const loadLatestItems = React.useCallback(async () => {
    const fetchOptions: GetFeedOptions = {
      perPage,
    };

    return await doFetch(fetchOptions);
  }, [doFetch, perPage]);

  /**
   * Load newer items that follow a certain id
   */
  const loadNewerItems = React.useCallback(async () => {
    const firstEntry = data?.[data?.length - 1];
    const id = firstEntry?.id;

    const fetchOptions: GetFeedOptions = {
      perPage,
    };

    if (order === 'asc') {
      fetchOptions.idGreaterThan = undefined;
      fetchOptions.idLessThan = id;
    }
    if (order === 'desc') {
      fetchOptions.idGreaterThan = id;
      fetchOptions.idLessThan = undefined;
    }

    return await doFetch(fetchOptions);
  }, [data, doFetch, order, perPage]);

  /**
   * Load older items that follow a certain id
   */

  const loadOlderItems = React.useCallback(async () => {
    const lastEntry = latestData.current?.[latestData.current?.length - 1];
    const id = lastEntry?.id;

    const fetchOptions: GetFeedOptions = {
      perPage,
    };

    if (order === 'desc') {
      fetchOptions.idGreaterThan = undefined;
      fetchOptions.idLessThan = id;
    }
    if (order === 'asc') {
      fetchOptions.idGreaterThan = id;
      fetchOptions.idLessThan = undefined;
    }

    return await doFetch(fetchOptions);
  }, [doFetch, latestData, order, perPage]);

  /**
   * Reload item by id
   */
  const loadItem = React.useCallback(
    async (id: string) => {
      const fetchOptions: GetFeedOptions = {
        perPage: 1,
      };

      fetchOptions.idLessOrEqualThan = id;

      const response = await doFetch(fetchOptions);
      addData(response, true);
    },
    [addData, doFetch]
  );

  /**
   * Reload item by id
   */
  const reloadItem = React.useCallback(
    async (id: string) => {
      const fetchOptions: GetFeedOptions = {
        perPage: 1,
      };

      fetchOptions.idLessOrEqualThan = id;

      const response = await doFetch(fetchOptions);
      updateData(response);
    },
    [doFetch, updateData]
  );

  /**
   * NOTE: this callback function is used asyncronously.
   * therefore we must make use of "latest" state variables
   */
  const checkShouldLoadMore = React.useCallback(async () => {
    const total = latestTotal.current;
    const hasMore = latestHasMore.current;
    const endReached = latestEndReached.current;
    const currentPosition = latestCurrentPosition.current;
    const sanitizedCursorPosition = latestSanitizedCursorPosition.current;

    // just skip loading if shouldSkipLoadMore is true
    // this is set to true after an entry is deleted at which point we won't have enough items to
    // display anymore but we also don't want to automatically trigger a request to load more items
    if (shouldSkipLoadMore) {
      setShouldSkipLoadMore(false);
      return;
    }

    // stop if we already determined there is no more data
    if (total !== undefined && !hasMore) {
      if (verbose) logger.info('hasMore stop');
      return;
    }

    // stop if endReached is true
    if (total === undefined && endReached) {
      if (verbose) logger.info('endReached stop');
      return;
    }
    // this stops loading more data is we have enough sanitized data to display
    if (sanitizedCursorPosition >= currentPosition + perPage) {
      if (verbose) logger.warn('sanitized data stop', { sanitizedCursorPosition, stop: currentPosition + perPage });
      return;
    }

    if (verbose)
      logger.info('SHOULD LOAD OLDER', {
        latestData: latestData.current?.length,
        latestCursorPosition: currentPosition + perPage,
        latestSanitizedCursorPosition: sanitizedCursorPosition,
        latestSanitizedData: latestSanitizedData.current?.length,
      });

    const response = await loadOlderItems();
    if (verbose) logger.info('LOADED OLDER', { length: response.length });

    addData(response);
    updateHasMore();
  }, [
    addData,
    latestCurrentPosition,
    latestData,
    latestEndReached,
    latestHasMore,
    latestSanitizedCursorPosition,
    latestSanitizedData,
    latestTotal,
    loadOlderItems,
    perPage,
    shouldSkipLoadMore,
    updateHasMore,
    verbose,
  ]);

  /**
   * Add a new item
   */
  const addItem = React.useCallback(
    (newData: D) => {
      const newItems = order === 'desc' ? [newData].concat(data) : data.concat([newData]);
      setNewData(newItems);
      if (total !== undefined) setTotal(total + 1);
      setCurrentPosition(currentPosition + 1);
      setSanitizedPosition(sanitizedPosition + 1);

      const sanitizedData = _sanitizeData(newItems);
      onChange?.(sanitizedData, total + 1);

      const newCursorPos = (sanitizedData?.length || 0) + perPage;
      setSanitizedCursorPosition(newCursorPos);
    },
    [order, data, setNewData, total, currentPosition, sanitizedPosition, _sanitizeData, onChange, perPage]
  );

  /**
   * Update an item
   */
  const updateItem = React.useCallback(
    (id: string, newData: D) => {
      const newItems = data.concat([]);
      const matchIndex = newItems.findIndex(item => item.id === id);
      let match = newItems[matchIndex];

      if (!match) return;
      // @ts-ignore
      match = deepMerge<D>(match, newData);

      newItems[matchIndex] = match;

      setNewData(newItems);
    },
    [data, setNewData]
  );

  /**
   * Delete an item
   */
  const deleteItem = React.useCallback(
    (id: string) => {
      const newAllItems = data.filter(item => item.id !== id);

      setShouldSkipLoadMore(true);
      setNewData(newAllItems);
      if (total !== undefined) setTotal(total - 1);

      const sanitizedData = _sanitizeData(newAllItems);
      onChange?.(sanitizedData, total - 1);
      setCurrentPosition(currentPosition > 0 ? currentPosition - 1 : 0);
      setSanitizedPosition(sanitizedPosition > 0 ? sanitizedPosition - 1 : 0);

      const newCursorPos = (sanitizedData?.length || 0) + perPage;
      setSanitizedCursorPosition(newCursorPos);

      // if this was the last item in the current list then try to load more items
      // if (newAllItems.length === 0) handleLoadMore();
    },
    [data, setNewData, total, _sanitizeData, onChange, currentPosition, sanitizedPosition, perPage]
  );

  // const loadAndAddLatestItems = React.useCallback(async () => {
  //   const response = await loadLatestItems();
  //   addData(response, true);
  // }, [addData, loadLatestItems]);

  const setError = React.useCallback(
    (id: string, errorId: string) => {
      const newErrors = JSON.parse(JSON.stringify(errors));
      newErrors[id] = errorId;
      setErrors(newErrors);
    },
    [errors]
  );

  const loadOlder = React.useCallback(async () => {
    const newPos = currentPosition + perPage;
    const finalPos = newPos > total ? total : newPos;
    setCurrentPosition(finalPos);

    const response = await loadOlderItems();
    addData(response);
    updateHasMore();

    const sanitizedData = _sanitizeData(data);
    setSanitizedPosition(sanitizedData?.length || 0);

    const newCursorPos = (sanitizedData?.length || 0) + perPage;
    setSanitizedCursorPosition(newCursorPos);

    if (!endReached && !latestEndReached.current) {
      setTimeout(() => {
        checkShouldLoadMore();
      }, timeoutDefaultValue);
    }
  }, [
    _sanitizeData,
    addData,
    checkShouldLoadMore,
    currentPosition,
    data,
    endReached,
    latestEndReached,
    loadOlderItems,
    perPage,
    total,
    updateHasMore,
  ]);

  const loadNewer = React.useCallback(async () => {
    const response = await loadNewerItems();
    addData(response, true);
  }, [loadNewerItems, addData]);

  const renderProps: StreamPaginationRenderCallbackProps<D[]> = React.useMemo(
    () => ({
      data: sanitizedData,
      sanitizedData: sanitizedData?.slice(0, sanitizedCursorPosition),
      isLoading,
      endReached,
      addItem,
      updateItem,
      deleteItem,
      reloadItem,
      loadOlder,
      loadOlderItems,
      loadLatestItems: async () => {
        const response = await loadLatestItems();
        addData(response, true);
      },
      currentPosition,
      perPage,
      // cursorPos: currentPosition + perPage,
      cursorPos: sanitizedCursorPosition,
      errors,
      setError,
    }),
    [
      addData,
      addItem,
      currentPosition,
      deleteItem,
      endReached,
      errors,
      isLoading,
      loadLatestItems,
      loadOlder,
      loadOlderItems,
      perPage,
      reloadItem,
      sanitizedCursorPosition,
      sanitizedData,
      setError,
      updateItem,
    ]
  );

  const onClickShowPrev = React.useCallback(async () => {
    const response = await loadLatestItems();
    addData(response, true);
  }, [addData, loadLatestItems]);

  const initialLogic = React.useCallback(async () => {
    if (highlightedId) {
      // just load a single item
      loadItem(highlightedId);
      return;
    }

    if (initialData) {
      addData(initialData);

      setSanitizedPosition(0);

      const sanitizedResponse = _sanitizeData(initialData);
      const newCursorPos = sanitizedResponse.length;
      setSanitizedCursorPosition(newCursorPos);

      setTimeout(() => {
        updateHasMore();

        // if initialFetch is true or all initial items were hidden
        // then we should try to load older data
        if (initialFetch || (initialData.length && newCursorPos === 0)) {
          loadOlder();
        }
      });
    } else {
      // if no initial data was passed perform an initial load
      const response = await loadOlderItems();

      if (response) {
        addData(response);

        setSanitizedPosition(0);

        const sanitizedResponse = _sanitizeData(response);
        const newCursorPos = sanitizedResponse.length;
        setSanitizedCursorPosition(newCursorPos);

        setTimeout(() => {
          updateHasMore();

          // if initialFetch is true we want to have at least enough items to fill a page
          if (initialFetch && (newCursorPos < perPage || sanitizedResponse.length === 0)) {
            loadOlder();
          }
        });
      }
    }
  }, [
    _sanitizeData,
    addData,
    highlightedId,
    initialData,
    initialFetch,
    loadItem,
    loadOlder,
    loadOlderItems,
    perPage,
    updateHasMore,
  ]);

  useEffectOnce(() => {
    initialLogic();
  });

  if (childrenOnly) return <>{children?.(renderProps)}</>;

  return (
    <div {...bem(null, [{ empty: sanitizedData?.length === 0 }], className)} {...otherProps}>
      <div {...bem('header')}>{header?.(renderProps)}</div>
      {/* show a loading spinner on initial load */}
      {isLoading && !data && <LoadingSpinner center />}
      {emptyDisplay && !isLoading && sanitizedData?.length === 0 && <div {...bem('empty')}>{emptyDisplay()}</div>}
      <div {...bem('main')}>
        {(!isLoading || data) && (
          <Debug
            data={{
              data: data?.length,
              currentPosition,
              sanitizedData: sanitizedData?.length,
              sanitizedPosition,
              sanitizedCursorPosition,
              initialTotal,
              total,
              endReached,
              hasMore,
            }}
          />
        )}

        {hasPrev && (
          <div {...bem('load-prev')}>
            {prevButton &&
              prevButton({
                isLoading,
                onClick: loadNewer,
                data,
                total,
                currentPosition,

                currentPos: currentPosition < data?.length ? currentPosition : data?.length,
              })}
            {!prevButton && (
              <Button loading={isLoading} onClick={onClickShowPrev}>
                Show Prev
              </Button>
            )}
          </div>
        )}

        {children?.(renderProps)}

        {hasNextButton && ((total !== undefined && hasMore) || (total === undefined && !endReached)) && (
          <div {...bem('load-next')}>
            {nextButton &&
              nextButton({
                isLoading,
                onClick: loadOlder,
                data,
                total,
                loadError,
                currentPos: currentPosition < data?.length ? currentPosition : data?.length,
              })}
            {!loadError && !nextButton && (
              <Button loading={isLoading} onClick={loadOlder}>
                Show More {/* NOTE: the total can only be shown if we have a total count */}
                {showTotal && total !== undefined && (
                  <span {...bem('load-more-counter')}>
                    {currentPosition}/{total}
                  </span>
                )}
              </Button>
            )}
          </div>
        )}
        {loadError && (
          <div {...bem('error')}>
            <span>
              <span>{loadError}</span>{' '}
              <Button variant="danger-link" onClick={() => void initialLogic()}>
                Try again
              </Button>
            </span>
          </div>
        )}
      </div>
      <div {...bem('footer')}>{footer?.(renderProps)}</div>
    </div>
  );
}

export const StreamPagination = React.forwardRef(StreamPaginationComp);
